/**
 * Helpers for the vault role list used on the Invite Members page.
 *
 * Role names come back from the API as "<vault_id>-User A",
 * "<vault_id>-User B", and so on. "User A" is the vault owner role and must
 * never be offered as an invitable role.
 *
 * This module has no imports so it can run under `node --test` without a
 * bundler, the same way card_expiry.mjs does.
 */

/**
 * Turn a role name into the token the app compares against.
 * "12-User A" with vault id 12 becomes "userA".
 *
 * With a vault id, only that vault's prefix is stripped, so "99-User A" seen
 * from vault 12 stays "99UserA" and the invite page keeps another vault's
 * roles apart. Without a vault id, any leading "<digits>-" is stripped. The
 * store uses that form for the user's own role: it used to strip only
 * "<current vault id>-", and on a direct page load the current vault is often
 * not set yet, which turned "74-User B" into "74UserB". No role matched, so
 * every v-is-authorized element on the page stayed hidden for that session.
 *
 * This mirrors what _.camelCase() produced for these role names, so the
 * matching behaviour is unchanged.
 */
const roleToken = (roleName, vaultId) => {
    let name = String(roleName ?? "");
    if (vaultId !== undefined && vaultId !== null && vaultId !== "") {
        name = name.replace(`${vaultId}-`, "");
    } else {
        name = name.replace(/^\d+-/, "");
    }
    const words = name.split(/[^a-zA-Z0-9]+/).filter((word) => word !== "");

    return words
        .map((word, index) => {
            const lower = word.toLowerCase();

            if (index === 0) {
                return lower;
            }

            return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join("");
};

/**
 * True when this role is the "User A" role of the given vault.
 */
const isUserARole = (role, vaultId) => {
    if (!role) {
        return false;
    }

    return roleToken(role.name, vaultId) === "userA";
};

/**
 * Return the role list without the "User A" role.
 *
 * Returns the list untouched when there is nothing to remove, so callers can
 * assign the result back without checking first. The previous inline version
 * read `.id` off an unmatched lookup and threw "Cannot read properties of
 * undefined (reading 'id')" whenever the roles had not loaded yet or "User A"
 * had already been removed (GLOBAL-VAULTS-204). Removing by index also avoids
 * the old splice(-1, 1) path, which silently deleted the last role instead.
 */
const withoutUserARole = (roles, vaultId) => {
    if (!Array.isArray(roles)) {
        return roles;
    }

    const index = roles.findIndex((role) => isUserARole(role, vaultId));

    if (index === -1) {
        return roles;
    }

    return roles.slice(0, index).concat(roles.slice(index + 1));
};

/**
 * True when inviting someone with this role needs a start/expiry date
 * (User E, User F and User G).
 *
 * Returns false when no role is picked yet. The invite row watchers call this
 * before a role is chosen, and the old inline code threw "Cannot read
 * properties of undefined (reading 'name')" (GLOBAL-VAULTS-20G).
 */
const roleNeedsExpiry = (role, vaultId) => {
    if (!role || !role.name) {
        return false;
    }

    return ["userE", "userF", "userG"].includes(roleToken(role.name, vaultId));
};

export { isUserARole, roleNeedsExpiry, roleToken, withoutUserARole };
